import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Card, CardContent, CardHeader, TextField } from '@mui/material';
import * as z from 'zod';
import LoaderOverlay from '@/components/ui/LoaderOverlay/LoaderOverlay.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useLocation, useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';

const resetPasswordSchema = z.object({
  password: z.string().min(1, { message: 'This field is required.' }),
});

type ResetPasswordSchema = z.infer<typeof resetPasswordSchema>;

const ResetPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { control, handleSubmit } = useForm<ResetPasswordSchema>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
    },
  });
  const {
    resetPasswordQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const isLoading = isLoadingResetPassword;
  const hasExpired = hash ? hash.includes('expired') : false;

  const handleSendResetPasswordLink = (data: ResetPasswordSchema) => {
    void (async () => {
      try {
        if (isLoading) return;

        await resetPassword({
          password: data.password,
        });

        navigate(Routes.HOME);
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <div className="flex grow justify-center items-center px-8 max-w-full">
      <form onSubmit={handleSubmit(handleSendResetPasswordLink)}>
        <LoaderOverlay isLoading={isLoading}>
          <Card elevation={10} className="rounded-xl w-[552px] max-w-full">
            <CardHeader title="Reset password" subheader="Enter new password" />
            <CardContent>
              <div className="flex flex-col gap-4">
                {isSuccessResetPassword && <Alert severity="success">Password successfully changed.</Alert>}
                {isErrorResetPassword && <Alert severity="error">{errorResetPassword?.message}</Alert>}
                {hasExpired && <Alert severity="error">Reset link has expired.</Alert>}
                <Controller
                  control={control}
                  name="password"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      value={value}
                      onChange={onChange}
                      disabled={isSuccessResetPassword || hasExpired}
                      type="password"
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.password')}
                      placeholder={t('placeholder.password')}
                    />
                  )}
                />
                <LoadingButton
                  loading={isLoading}
                  disabled={isSuccessResetPassword || hasExpired}
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                >
                  {t('button.resetPassword')}
                </LoadingButton>
              </div>
            </CardContent>
          </Card>
        </LoaderOverlay>
      </form>
    </div>
  );
};

export default ResetPasswordPage;
